import * as React from 'react';
import { Box, useMediaQuery, useTheme } from '@mui/material';
import Button from '@mui/material/Button';
import Container from '@mui/material/Container';
import InputLabel from '@mui/material/InputLabel';
import Link from '@mui/material/Link';
import Stack from '@mui/material/Stack';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { visuallyHidden } from '@mui/utils';
import { styled } from '@mui/material/styles'; // Import useTheme hook
import Globe from 'react-globe.gl';
import EmailModal from './EmailModal'; // Import the EmailModal component

const StyledBox = styled('div')(({ theme }) => ({
    alignSelf: 'center',
    width: '100%',
    height: 400,
    marginTop: theme.spacing(8),
    borderRadius: (theme.vars || theme).shape.borderRadius,
    outline: '6px solid',
    outlineColor: 'hsla(220, 25%, 80%, 0.2)',
    border: '1px solid',
    borderColor: (theme.vars || theme).palette.grey[200],
    boxShadow: '0 0 12px 8px hsla(220, 25%, 80%, 0.2)',
    backgroundSize: 'cover',
    [theme.breakpoints.up('sm')]: {
        marginTop: theme.spacing(10),
        height: 700,
    },
    ...theme.applyStyles('dark', {
        boxShadow: '0 0 24px 12px hsla(210, 100%, 25%, 0.2)',
        outlineColor: 'hsla(220, 20%, 42%, 0.1)',
        borderColor: (theme.vars || theme).palette.grey[700],
    }),
    backgroundColor: 'inherit', // Inherit background color from parent
}));

export default function Hero() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm')); // 'sm' breakpoint for mobile devices
    const [isModalOpen, setIsModalOpen] = React.useState(false); // State for managing modal open/close

    const myData = [
        { startLat: 37.6889, startLng: -97.3361, endLat: 36.1627, endLng: -86.7816, color: '#FF6347' }, // Wichita to Nashville
        { startLat: 37.6889, startLng: -97.3361, endLat: 25.276987, endLng: 55.296249, color: '#4682B4' }, // Wichita to Dubai
        { startLat: 37.6889, startLng: -97.3361, endLat: 33.5138, endLng: 36.2765, color: '#32CD32' }, // Wichita to Syria
        { startLat: 37.6889, startLng: -97.3361, endLat: 1.2921, endLng: 36.8219, color: '#FFD700' }, // Wichita to Nairobi
        { startLat: 36.1627, startLng: -86.7816, endLat: -33.9249, endLng: 18.4241, color: '#FF4500' }, // Nashville to Cape Town
        { startLat: 37.6889, startLng: -97.3361, endLat: 9.05785, endLng: 38.7637, color: '#FF1493' }, // Wichita to Addis Ababa, Ethiopia
        { startLat: 37.6889, startLng: -97.3361, endLat: 5.6037, endLng: -0.187, color: '#1E90FF' }, // Wichita to Accra, Ghana
        { startLat: 37.6889, startLng: -97.3361, endLat: 12.9716, endLng: 77.5946, color: '#32CD32' }, // Wichita to Johannesburg, South Africa
        { startLat: 37.6889, startLng: -97.3361, endLat: 6.5244, endLng: 3.3792, color: '#FFD700' }, // Wichita to Lagos, Nigeria
        { startLat: 37.6889, startLng: -97.3361, endLat: 9.082, endLng: 38.746, color: '#FF8C00' }, // Wichita to Dar es Salaam, Tanzania
        { startLat: 37.6889, startLng: -97.3361, endLat: 33.5898, endLng: -7.6039, color: '#800080' }, // Wichita to Casablanca, Morocco
    ];

    const handleModalOpen = () => setIsModalOpen(true); // Open the modal
    const handleModalClose = () => setIsModalOpen(false); // Close the modal


    return (
        <Box
            id="hero"
            sx={(theme) => ({
                width: '100%',
                display: 'flex',
                flexDirection: { xs: 'column', sm: 'row' },
                alignItems: 'center',
                backgroundRepeat: 'no-repeat',
                backgroundImage:
                    'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 90%), transparent)',
                ...theme.applyStyles('dark', {
                    backgroundImage:
                        'radial-gradient(ellipse 80% 50% at 50% -20%, hsl(210, 100%, 16%), transparent)',
                    fontFamily: 'sans-serif', // Minimalistic font

                }),
            })}
        >
            <Container
                sx={{
                    display: 'flex',
                    flexDirection: 'row',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    pt: { xs: 14, sm: 20 },
                    pb: { xs: 8, sm: 12 },
                    width: '100%',
                    fontFamily: 'sans-serif', // Minimalistic font

                }}
            >
                {/* Left Section: Text and Email Contact */}
                <Box sx={{ width: { xs: '100%', sm: '50%' } }}>
                    <Stack
                        spacing={2}
                        useFlexGap
                        sx={{ alignItems: 'flex-start', width: '100%' }}
                    >
                        <Typography
                            variant="h1"
                            sx={{
                                display: 'flex',
                                flexDirection: { xs: 'column', sm: 'row' },
                                alignItems: 'center',
                                fontSize: 'clamp(3rem, 10vw, 3.5rem)',
                                fontFamily: 'sans-serif', // Minimalistic font

                            }}
                        >
                            Better&nbsp;than&nbsp;
                            <Typography
                                component="span"
                                variant="h1"
                                sx={(theme) => ({
                                    fontSize: 'inherit',
                                    fontFamily: 'sans-serif', // Minimalistic font

                                    color: 'primary.main',
                                    ...theme.applyStyles('dark', {
                                        color: 'primary.light',
                                    }),
                                })}
                            >
                                recycling.
                            </Typography>
                        </Typography>
                        <Typography
                            sx={{
                                textAlign: 'left',
                                fontFamily: 'sans-serif', // Minimalistic font

                                color: 'text.secondary',
                                width: { sm: '100%', md: '80%' },
                                fontSize: { xs: '1rem', sm: '1.2rem', md: '1.3rem' }, // Adjusting font size responsively
                            }}
                        >
                            At <b>refrb</b>, we repurpose unused or discarded technology from businesses and institutions,
                            refurbishing it into fully functional devices that we then send to schools in developing
                            countries. Our goal is to provide students in low-income communities with the tools they
                            need to thrive academically and beyond.
                        </Typography>
                        <Stack
                            direction={{ xs: 'column', sm: 'row' }}
                            spacing={1}
                            useFlexGap
                            sx={{ pt: 2, width: { xs: '100%', sm: '350px' } }}
                        >
                            <InputLabel htmlFor="email-hero" sx={visuallyHidden}>
                                Email
                            </InputLabel>
                            <TextField
                                id="email-hero"
                                hiddenLabel
                                size="small"
                                variant="outlined"
                                aria-label="Enter your email address"
                                placeholder="Your email address"
                                fullWidth
                                slotProps={{
                                    htmlInput: {
                                        autoComplete: 'off',
                                        fontFamily: 'sans-serif', // Minimalistic font

                                        'aria-label': 'Enter your email address',
                                    },
                                }}
                            />
                            <EmailModal open={isModalOpen} onClose={handleModalClose} />

                        </Stack>
                        <Typography
                            variant="caption"

                            color="text.secondary"
                            sx={{
                                textAlign: 'left', fontFamily: 'sans-serif', // Minimalistic font
                            }}
                        >
                            Have questions? Feel free to&nbsp;
                            <Link href="mailto:your-email@example.com" color="primary">
                                reach out to us.
                            </Link>
                        </Typography>
                    </Stack>
                </Box>

                {/* Right Section: World Animation */}
                {!isMobile && (
                    <Box>
                        <Globe
                            globeImageUrl="https://unpkg.com/three-globe@2.39.1/example/img/earth-day.jpg"
                            arcsData={myData}
                            arcColor={() => ['#FF6347', '#4682B4']} // Color transition between red and blue
                            arcDashLength={() => Math.random()}
                            arcDashGap={() => Math.random()}
                            width={600}
                            height={600}
                            arcWidth={10} // Set a fixed width for arcs
                            arcDashAnimateTime={2000} // Time for dash animation
                            showGraticules
                            graticuleLineColor="rgba(255,255,255,0.5)"
                            atmosphereColor="rgba(0,0,0,0.1)"
                            backgroundColor="rgba(0,0,0,0)"
                        />
                    </Box>
                )}
            </Container>
        </Box>
    );
}
